import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, Crown, Link2, UserCheck } from "lucide-react";
import api from "../api";

export default function PaymentResult({ variant }) {
  const [params] = useSearchParams();
  const [state, setState] = useState(variant === "cancel" ? "cancelled" : "checking");
  const [linked, setLinked] = useState(false);
  const [linkedName, setLinkedName] = useState("");
  const sessionId = params.get("session_id");

  useEffect(() => {
    const token = params.get("token");
    if (token) localStorage.setItem("gm_member_token", token);
    if (params.get("linked") === "1") setLinked(true);
  }, [params]);

  useEffect(() => {
    if (variant !== "success") return;
    let attempts = 0;
    const poll = async () => {
      try {
        if (!sessionId) return setState("failed");
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setState("paid");
          if (res.data.discord_username) {
            setLinked(true);
            setLinkedName(res.data.discord_username);
            return;
          }
          const mt = localStorage.getItem("gm_member_token");
          if (mt) {
            const link = await api.post("/payments/link", { session_id: sessionId }, { headers: { Authorization: `Bearer ${mt}` } });
            setLinked(true);
            setLinkedName(link.data.discord_username);
          }
          return;
        }
      } catch (e) {
        if (attempts > 2 && e?.response?.status === 404) return setState("failed");
      }
      attempts += 1;
      if (attempts < 8) setTimeout(poll, 2000);
      else setState("failed");
    };
    poll();
  }, [variant, sessionId]);

  const linkDiscord = async () => {
    const res = await api.get(`/auth/discord/member/url?state=${encodeURIComponent(`premium:${sessionId}`)}`);
    window.location.href = res.data.url;
  };

  return (
    <main className="min-h-screen flex items-center justify-center hero-glow px-5 pt-16" data-testid={`payment-${variant}-page`}>
      <div className="max-w-md w-full card-beam rounded-2xl bg-panel border border-white/10 p-10 text-center">
        {state === "checking" && (
          <>
            <Loader2 size={40} className="mx-auto text-blurple animate-spin" />
            <h1 className="font-heading text-2xl font-bold text-white mt-6">Confirming your payment…</h1>
            <p className="text-sm text-muted-foreground mt-2">Hold tight, we're talking to the payment provider.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 size={44} className="mx-auto text-emerald-400" />
            <h1 className="font-heading text-2xl font-bold text-white mt-6" data-testid="payment-success-title">Premium activated</h1>
            {linked ? (
              <div className="mt-4 rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-300 flex items-center justify-center gap-2" data-testid="payment-linked">
                <UserCheck size={15} /> Linked to Discord{linkedName ? ` as ${linkedName}` : ""} — the bot unlocks your perks automatically.
              </div>
            ) : (
              <div className="mt-4 rounded-lg border border-amber-400/30 bg-amber-400/10 px-4 py-4" data-testid="payment-link-prompt">
                <p className="text-sm text-amber-200 mb-3">Last step — link your Discord so GrooveMusic knows it's you.</p>
                <button onClick={linkDiscord} data-testid="payment-link-discord"
                  className="inline-flex items-center gap-2 bg-blurple hover:bg-[#4752c4] text-white text-sm font-heading font-bold px-6 py-2.5 rounded-full transition-colors duration-200">
                  <Link2 size={14} /> Link my Discord
                </button>
              </div>
            )}
            <p className="text-sm text-muted-foreground mt-4 leading-relaxed">
              Run <code className="font-mono text-volt">!premium</code> in any server to see your perks live.
            </p>
            <Link to="/" data-testid="payment-home-link" className="mt-6 inline-flex items-center gap-2 border border-white/20 hover:border-blurple text-white font-heading font-semibold px-8 py-3.5 rounded-full transition-colors duration-200">
              <Crown size={16} /> Back to GrooveMusic
            </Link>
          </>
        )}
        {state === "cancelled" && (
          <>
            <XCircle size={44} className="mx-auto text-white/40" />
            <h1 className="font-heading text-2xl font-bold text-white mt-6" data-testid="payment-cancel-title">Checkout cancelled</h1>
            <p className="text-sm text-muted-foreground mt-2">No charge was made. The free tier is still all yours.</p>
            <Link to="/premium" data-testid="payment-retry-link" className="mt-8 inline-flex items-center gap-2 border border-white/20 hover:border-blurple text-white font-heading font-semibold px-8 py-3.5 rounded-full transition-colors duration-200">
              Back to Premium
            </Link>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle size={44} className="mx-auto text-red-400" />
            <h1 className="font-heading text-2xl font-bold text-white mt-6">Payment not confirmed</h1>
            <p className="text-sm text-muted-foreground mt-2">If you were charged, contact staff from the Support page with your receipt.</p>
            <Link to="/support" className="mt-8 inline-flex items-center gap-2 border border-white/20 hover:border-blurple text-white font-heading font-semibold px-8 py-3.5 rounded-full transition-colors duration-200">
              Get help
            </Link>
          </>
        )}
      </div>
    </main>
  );
}
